// Comprehension Module Data Structure
export const comprehensionQuestionTypes = [
  { 
    value: 'MC', 
    label: 'Multiple Choice', 
    description: 'Select one correct answer from 4 options' 
  },
  { 
    value: 'MS', 
    label: 'Multiple Select', 
    description: 'Select multiple correct answers' 
  },
  { 
    value: 'FB', 
    label: 'Fill in the Blank', 
    description: 'Complete missing words or phrases' 
  },
  { 
    value: 'SQ', 
    label: 'Short Answer', 
    description: 'Brief written response' 
  },
  { 
    value: 'MQ', 
    label: 'Multipart Question', 
    description: 'Question with multiple parts' 
  },
  { 
    value: 'TF', 
    label: 'True/False/Not Given', 
    description: 'Evaluate statement accuracy' 
  },
  { 
    value: 'TB', 
    label: 'Table Completion', 
    description: 'Fill in table with information' 
  },
  { 
    value: 'MT', 
    label: 'Matching', 
    description: 'Match items from two columns' 
  },
  { 
    value: 'OR', 
    label: 'Ordering/Sequencing', 
    description: 'Arrange items in correct order' 
  },
  { 
    value: 'GA', 
    label: 'Gap Analysis', 
    description: 'Fill gaps with provided words' 
  },
  { 
    value: 'WC', 
    label: 'Word Count Limited', 
    description: 'Response within word limit' 
  }
];

// Mock comprehension passages for testing
export const mockComprehensionPassages = [
  {
    id: 'passage-1',
    title: 'Customer Service Complaint',
    content: `To whom it may concern,

I am writing to complain about the appalling service I experienced in your Causeway Bay bookshop on Saturday 11 September 20__.

I was looking for a Spanish learning book and found a copy on the shelf. However, it was quite tatty and had some pages missing. I asked a staff member if they had a new copy available. He rolled his eyes and said they only had the display copy. When I asked if I could get a discount since it was damaged, he scowled and said no discount was available. I then asked if they could order a new copy for me, and he said it would take six weeks to arrive.

I was horrified by his unhelpful attitude. I did not feel welcome in your shop at all. I ended up buying the book from a competitor whose staff were very friendly and helpful.

I would appreciate a letter of apology from your management team.`,
    wordCount: 293,
    difficulty: 'intermediate',
    category: 'business',
    tags: ['customer service', 'complaint', 'business communication']
  },
  {
    id: 'passage-2',
    title: 'Environmental Conservation',
    content: `Climate change represents one of the most pressing challenges of our time. The scientific consensus is clear: human activities, particularly the burning of fossil fuels, have significantly contributed to the warming of our planet.

The consequences of climate change are far-reaching and include rising sea levels, more frequent and severe weather events, and disruptions to ecosystems worldwide. These changes affect not only the environment but also human societies, economies, and health.

Scientists have identified several key strategies for addressing climate change. These include transitioning to renewable energy sources, improving energy efficiency, and implementing sustainable practices in agriculture and industry. Additionally, protecting and restoring natural ecosystems, such as forests and wetlands, can help absorb carbon dioxide from the atmosphere.

Individual actions also play a crucial role. Simple changes like reducing energy consumption, choosing sustainable transportation options, and supporting environmentally responsible businesses can collectively make a significant difference.

The urgency of this issue requires immediate action from governments, businesses, and individuals alike. While the challenge is daunting, the solutions are within our reach if we act decisively and collaboratively.`,
    wordCount: 245,
    difficulty: 'advanced',
    category: 'environment',
    tags: ['climate change', 'environment', 'science', 'sustainability']
  },
  {
    id: 'passage-3',
    title: 'Technology and Education',
    content: `The integration of technology in education has transformed the way students learn and teachers instruct. Digital tools and online platforms have made education more accessible, interactive, and personalized than ever before.

One of the most significant benefits of educational technology is its ability to provide personalized learning experiences. Adaptive learning software can adjust to each student's pace and learning style, ensuring that no student is left behind or held back. This individualized approach helps students build confidence and achieve their full potential.

Online learning platforms have also expanded access to education globally. Students in remote areas can now access high-quality educational resources that were previously unavailable to them. This democratization of education has the potential to reduce educational inequalities worldwide.

However, the digital divide remains a significant challenge. Not all students have equal access to technology and reliable internet connections. This inequality can exacerbate existing educational disparities, making it crucial for policymakers and educators to address these access issues.

Despite these challenges, the benefits of educational technology are undeniable. As technology continues to evolve, it will likely play an even more central role in shaping the future of education.`,
    wordCount: 267,
    difficulty: 'intermediate',
    category: 'education',
    tags: ['technology', 'education', 'digital learning', 'accessibility']
  }
];

// Mock generated questions for testing
export const mockGeneratedQuestions = [
  {
    id: 'q1',
    questionNumber: 1,
    paragraph: 1,
    type: 'MC',
    question: 'What is the main purpose of the letter?',
    options: [
      'To request a refund',
      'To complain about poor service',
      'To order a new book',
      'To provide feedback'
    ],
    correctAnswer: 1,
    explanation: 'The letter clearly states it is a complaint about the appalling service experienced in the bookshop.'
  },
  {
    id: 'q2',
    questionNumber: 2,
    paragraph: 2,
    type: 'FB',
    question: 'Complete the sentence: The staff member said they only had the _____ copy.',
    correctAnswer: 'display',
    explanation: 'The text states "He rolled his eyes and said they only had the display copy."'
  },
  {
    id: 'q3',
    questionNumber: 3,
    paragraph: 2,
    type: 'TF',
    question: 'The staff member offered a discount for the damaged book.',
    correctAnswer: 'False',
    explanation: 'The text states "he scowled and said no discount was available."'
  },
  {
    id: 'q4',
    questionNumber: 4,
    paragraph: 3,
    type: 'OR',
    question: 'Arrange the following events in the correct order:',
    events: [
      'Customer found a tatty book',
      'Customer asked for a new copy',
      'Customer asked for a discount',
      'Customer bought from competitor'
    ],
    correctOrder: [1, 2, 3, 4],
    explanation: 'The events occurred in this sequence as described in the passage.'
  },
  {
    id: 'q5',
    questionNumber: 5,
    paragraph: 1,
    type: 'SQ',
    question: 'What was the customer looking for in the bookshop?',
    correctAnswer: 'A Spanish learning book',
    explanation: 'The text states "I was looking for a Spanish learning book."'
  }
];

// API response structure for DeepSeek integration
export const comprehensionApiResponseStructure = {
  success: true,
  questions: [
    {
      id: 'generated-question-id',
      questionNumber: 1,
      paragraph: 1,
      type: 'MC', // Question type code
      question: 'Generated question text',
      options: ['Option A', 'Option B', 'Option C', 'Option D'], // For MC questions
      correctAnswer: 0, // Index of correct answer for MC, or correct answer text for others
      explanation: 'Explanation of why this answer is correct',
      difficulty: 'intermediate',
      tags: ['vocabulary', 'comprehension']
    }
  ],
  metadata: {
    totalQuestions: 10,
    passageWordCount: 293,
    generationTime: '2.5s',
    difficulty: 'intermediate',
    categories: ['business', 'communication']
  }
};

// Helper functions for API integration
export const formatComprehensionPrompt = (passage, quizConfig) => {
  const questionTypes = quizConfig.questions.map(q => q.type).join(', ');
  const totalQuestions = quizConfig.totalQuestions;
  
  return `Generate ${totalQuestions} reading comprehension questions based on the following passage. 

IMPORTANT: You must respond with ONLY valid JSON format. Do not include any explanatory text before or after the JSON.

Passage:
${passage}

Question types to include: ${questionTypes}

Requirements:
1. Generate exactly ${totalQuestions} questions
2. Include a mix of the specified question types
3. Each question should test different aspects of comprehension
4. Provide clear, unambiguous correct answers
5. Include explanations for each answer

Respond with ONLY this JSON structure:
{
  "questions": [
    {
      "questionNumber": 1,
      "paragraph": 1,
      "type": "MC",
      "question": "Question text",
      "options": ["A", "B", "C", "D"],
      "correctAnswer": 0,
      "explanation": "Explanation"
    }
  ]
}`;
};

export const parseComprehensionResponse = (apiResponse) => {
  try {
    console.log('Raw API Response:', apiResponse);
    
    // Handle different response formats
    let data;
    if (typeof apiResponse === 'string') {
      // Try to extract JSON from the response string
      const jsonMatch = apiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        data = JSON.parse(jsonMatch[0]);
      } else {
        // If no JSON found, try to parse the entire string
        data = JSON.parse(apiResponse);
      }
    } else {
      data = apiResponse;
    }
    
    console.log('Parsed Data:', data);
    
    // Handle different response structures
    let questions = [];
    if (data.questions && Array.isArray(data.questions)) {
      questions = data.questions;
    } else if (data.content && data.content.questions) {
      questions = data.content.questions;
    } else if (Array.isArray(data)) {
      questions = data;
    } else if (data.response && Array.isArray(data.response)) {
      questions = data.response;
    }
    
    // Validate and format questions
    const formattedQuestions = questions.map((q, index) => ({
      id: `q${index + 1}`,
      questionNumber: index + 1,
      paragraph: q.paragraph || 1,
      type: q.type || 'MC',
      question: q.question || q.text || '',
      options: q.options || [],
      correctAnswer: q.correctAnswer || q.answer || '',
      explanation: q.explanation || q.reason || '',
      difficulty: q.difficulty || 'intermediate'
    }));
    
    return {
      success: true,
      questions: formattedQuestions,
      metadata: {
        totalQuestions: formattedQuestions.length,
        generationTime: data.generationTime || 'unknown',
        difficulty: data.difficulty || 'intermediate'
      }
    };
  } catch (error) {
    console.error('Error parsing comprehension response:', error);
    console.error('Response that failed to parse:', apiResponse);
    return {
      success: false,
      error: 'Failed to parse API response',
      questions: []
    };
  }
};

// Export all data for easy access
export const comprehensionData = {
  questionTypes: comprehensionQuestionTypes,
  mockPassages: mockComprehensionPassages,
  mockQuestions: mockGeneratedQuestions,
  apiStructure: comprehensionApiResponseStructure
}; 